import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { distinctUntilChanged, filter, finalize, startWith, switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { BehaviorSubject, interval, ReplaySubject } from 'rxjs';
import { isEmpty, isEqual } from 'lodash';
import { Store } from '@ngrx/store';

import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { StripePaymentService } from 'projects/workspace/src/app/shared/services';
import { AdministrationsApiService } from 'projects/workspace/src/app/administration/services/administrations-api.service';
import { SubscriptionActivationTabEnum, SubscriptionPlanEnum } from '../subscription-activation.emum';
import { SubscriptionActivationPricesModel } from '../subscription-activation-prices.model';
import { ToasterService } from '../../ui-components/toaster';
import { BACKGROUND_CONTENT, TimeOfDay } from '../../modals/modals-sign-in/sign-in.config';
import { AuthService } from '../../../auth/auth.service';
import { HrmService } from 'projects/workspace/src/app/hrm/hrm.service';
import { AccountingSettingsModel } from 'projects/workspace/src/app/accounting/accounting-settings-module/models';
import { CommonModalsActionsEnum, InfoModalComponent } from '../../modals/modals-common';
import { AccountingSettingsService } from 'projects/workspace/src/app/accounting/accounting-settings-module/services';

@Component({
  selector: 'rnpl-subscription-activation',
  templateUrl: './subscription-activation.component.html',
  styleUrls: ['./subscription-activation.component.scss']
})
export class SubscriptionActivationComponent implements OnInit, OnDestroy, AfterViewInit {
  public activationError: boolean = false;
  public today: Date = new Date();
  public companyProfile: CompanyProfile;
  public accountingSettings: AccountingSettingsModel;
  public form: FormGroup;
  public subscriptionPrices: SubscriptionActivationPricesModel;
  public promoCodeControl: FormControl = new FormControl();
  public promoApplied: boolean = false;
  public lastValidPromocode: string = null;

  public backGroundContent = BACKGROUND_CONTENT;
  public timeOfDay = TimeOfDay();

  public activationTabEnum = SubscriptionActivationTabEnum;

  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  public activeTab$: BehaviorSubject<SubscriptionActivationTabEnum> =
    new BehaviorSubject<SubscriptionActivationTabEnum>(SubscriptionActivationTabEnum.HELLO);
  public activateSubscriptionRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public continueRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public promoCodeApplyingRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public tabsDataByActiveTab = {
    [SubscriptionActivationTabEnum.SUBSCRIPTION_OPTIONS]: {
      title: 'SUBSCRIPTION_PLAN.SELECT_SUBSCRIPTION',
      description: 'SUBSCRIPTION_PLAN.SELECT_SUBSCRIPTION_OPTIONS_DESC',
      tabName: SubscriptionActivationTabEnum.SUBSCRIPTION_OPTIONS,
    },
    [SubscriptionActivationTabEnum.CARD_DETAILS]: {
      title: 'SUBSCRIPTION_PLAN.SPECIFY_CARD_DETAILS',
      description: 'SUBSCRIPTION_PLAN.BILLING_OPTIONS_DESC',
      tabName: SubscriptionActivationTabEnum.CARD_DETAILS,
    },
  };

  @ViewChild('subscriptionOptionsComponent', {static: false}) public subscriptionOptionsComponentRef;
  @ViewChild('perfectScrollbarSummary', {static: false}) public perfectScrollbarSummaryRef;
  @ViewChild('perfectScrollbarContent', {static: false}) public perfectScrollbarContentRef;

  constructor(
    public dialogRef: MatDialogRef<SubscriptionActivationComponent>,
    public dialog: MatDialog,
    public fb: FormBuilder,
    public toasterService: ToasterService,
    public stripePaymentService: StripePaymentService,
    public store: Store<AppState>,
    private router: Router,
    private authService: AuthService,
    private hrmService: HrmService,
    public administrationsApiService: AdministrationsApiService,
    public accountingSettingsService: AccountingSettingsService,
    @Inject(MAT_DIALOG_DATA) public data: {
      activeTab: SubscriptionActivationTabEnum,
      initStripe: boolean,
    }
  ) {
    this.accountingSettingsService.getAccountingSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings: AccountingSettingsModel) => {
        this.accountingSettings = settings;
      });

    this.initForm();
    if (this.data) {
      if (this.data.activeTab) {
        this.activeTab$.next(this.data.activeTab);
      }
      if (this.data.initStripe) {
        this.initStripe();
      }
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      // fix scroll visibility issue
      this.perfectScrollbarSummaryRef.directiveRef.update();
      this.perfectScrollbarContentRef.directiveRef.update();
    }, 300);
  }

  ngOnInit() {
    this.store
      .select(selectCompanyProfile)
      .pipe(
        distinctUntilChanged(isEqual),
        filter(profile => !isEmpty(profile)),
        takeUntil(this.destroy$)
      ).subscribe((profile: CompanyProfile) => {
        this.companyProfile = profile;
        this.form.patchValue(profile, {emitEvent: true});
      });
  }

  public initForm(): void {
    this.form = this.fb.group({
      subscriptionManagement: this.fb.group(({
        // userChannelOptions: this.fb.group({
        //   wholesalesEnabled: [],
        //   ecommerceEnabled: [],
        //   servicesEnabled: [],
        // }),
        subscriptionPlan: [],
        extensionsUser: this.fb.group({
          inventoryManagement: [],
          onlineSales: [],
          timeTracking: [],
        }),
      })),
      // billingDetails: this.fb.group(({
      //   billingFrequency: [],
      //   paymentMethod: [],
      // }))
    });

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.getPrices());

    this.subscriptionPlanControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: SubscriptionPlanEnum) => {
        // if (value === SubscriptionPlanEnum.TEAM_MONTHLY && value === this.companyProfile.subscriptionManagement.subscriptionPlanActive) {
        //   this.form.get('subscriptionManagement').get('userChannelOptions')
        //     .patchValue(this.companyProfile.subscriptionManagement.userChannelOptions, {emitEvent: false});
        // } else {
        //
        // }
        if (this.promoCodeControl && this.promoCodeControl.errors && this.promoCodeControl.errors.validation) {
          this.promoCodeControl.patchValue(null);
        }
        this.getPrices();
      });
  }

  public getPrices(): void {
    this.stripePaymentService.getCompanySubscriptionPrices(this.getUpdatedCompanyProfile())
      .pipe(takeUntil(this.destroy$))
      .subscribe((prices: SubscriptionActivationPricesModel) => this.subscriptionPrices = prices);
  }

  public getUpdatedCompanyProfile(forcePromoCode = false): CompanyProfile {
    if (!this.companyProfile) { return null; }
    const subscriptionManagementData = this.form.getRawValue();
    return {
      ...this.companyProfile,
      subscriptionManagement: {
        ...this.companyProfile.subscriptionManagement,
        promoCode: forcePromoCode
          ? this.promoCodeControl.value
          : (
              (
                this.promoCodeControl.errors
                && (this.promoCodeControl.errors.validation || (this.promoCodeControl.errors.notChecked && !this.lastValidPromocode))
              )
              || !this.promoApplied
            )
              ? null
              : this.lastValidPromocode,
        subscriptionPlan: subscriptionManagementData.subscriptionManagement.subscriptionPlan,
        extensionsUser: {
          ...this.companyProfile.subscriptionManagement.extensionsUser,
          ...subscriptionManagementData.subscriptionManagement.extensionsUser
        }
      },
      // billingDetails: {
      //   ...this.companyProfile.billingDetails,
      //   ...subscriptionManagementData.billingDetails
      // }
    };
  }

  public closeModal(): void {
    this.dialogRef.close();
  }

  public selectTab(tabName: SubscriptionActivationTabEnum): void {
    if (!!tabName && this.activeTab$.getValue() !== tabName) {
      this.activeTab$.next(tabName);
    }
  }

  public openCardDetails(skipWarnings = false): void {
    if (this.shopifyBilling) {
      this.continueRequest$.next(true);
      const updatedProfile = this.getUpdatedCompanyProfile();

      this.administrationsApiService.generateShopifyConfirmationUrl(
        this.companyProfile.workspaceId,
        {
          plan: updatedProfile.subscriptionManagement.subscriptionPlan,
          extensions: updatedProfile.subscriptionManagement.extensionsUser,
          promoCode: updatedProfile.subscriptionManagement.promoCode
        }
      ).pipe(
        finalize(() => this.continueRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((res: { confirmationUrl: string }) => {
        window.open(res.confirmationUrl, '_top');
      });
      return;
    }
    if (!this.validatePlan()) {
      return;
    }

    if (
      !skipWarnings &&
      (this.isPlanControlValueBasicPlan) &&
      this.accountingSettings.reportingSettings.forwardReportingType === 'STANDARD' &&
      this.companyProfile.legalType.simplifiedReportingAllowed
    ) {
      const dialog = this.dialog.open(InfoModalComponent, {
        data: {
          title: 'SUBSCRIPTION_PLAN.DOWNGRADE_PLAN_MODAL_TTL',
          message: 'SUBSCRIPTION_PLAN.DOWNGRADE_PLAN_MODAL_MSG',
          confirmBtnText: 'BUTTON.CONTINUE',
          confirmBtnIcon: 'arrow-right'
        }
      });

      dialog.afterClosed().subscribe((res: CommonModalsActionsEnum) => {
        if (res === CommonModalsActionsEnum.CONFIRM) {
          this.openCardDetails(true);
        }
      });
        return;
    }

    this.continueRequest$.next(true);

    this.administrationsApiService.updateCompanyProfileSbcManagement(this.getUpdatedCompanyProfile())
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const cbFn = () => this.selectTab(this.activationTabEnum.CARD_DETAILS);
        const onReadyFn = () => this.continueRequest$.next(false);
        this.initStripe(cbFn, onReadyFn);
      }, () => {
        this.continueRequest$.next(false);
      });
  }

  public initStripe(cbFunction?: Function, onReadyFn?: Function): void {
    if (this.shopifyBilling) { return; }
    this.stripePaymentService.initStripe(this.lastValidPromocode, cbFunction, onReadyFn);
  }

  public cancelStripeRequest(): void {
    this.stripePaymentService.cancelPaymentIntent()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.selectTab(this.activationTabEnum.SUBSCRIPTION_OPTIONS);
      });
  }

  public applyPromoCode(): void {
    if (!this.promoCodeControl.value) {
      this.promoCodeControl.setErrors({validation: 'PROMOCODE.ENTER_PROMO_CODE', notChecked: false});
      return;
    }
    if (this.promoCodeApplyingRequest$.getValue()) { return; }
    this.promoCodeApplyingRequest$.next(true);

    this.stripePaymentService.getCompanySubscriptionPrices(
      this.getUpdatedCompanyProfile(true),
      this.activeTab$.getValue() === this.activationTabEnum.CARD_DETAILS ? this.stripePaymentService.paymentIntentId : null
    )
      .pipe(
        finalize(() => this.promoCodeApplyingRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((prices: SubscriptionActivationPricesModel) => {
        this.subscriptionPrices = prices;
        this.promoApplied = true;
        this.lastValidPromocode = this.promoCodeControl.value;
        this.promoCodeControl.setErrors({notChecked: false});
      }, (error) => {
        this.promoCodeControl.setErrors({validation: error.error.errors[0], notChecked: false});
      });
  }

  public resetPromoCodeErrors(): void {
    this.promoCodeControl.setErrors({notChecked: true, validation: false});
  }

  public activateSubscription(skipWarnings = false): void {
    if (!this.validatePlan()) {
      return;
    }

    if (
      !skipWarnings &&
      (this.isFreePlan) &&
      this.accountingSettings.reportingSettings.forwardReportingType === 'STANDARD' &&
      this.companyProfile.legalType.simplifiedReportingAllowed
    ) {
      const dialog = this.dialog.open(InfoModalComponent, {
        data: {
          title: 'SUBSCRIPTION_PLAN.DOWNGRADE_PLAN_MODAL_TTL',
          message: 'SUBSCRIPTION_PLAN.DOWNGRADE_PLAN_MODAL_MSG',
          confirmBtnText: 'BUTTON.CONTINUE',
          confirmBtnIcon: 'arrow-right'
        }
      });

      dialog.afterClosed().subscribe((res: CommonModalsActionsEnum) => {
        if (res === CommonModalsActionsEnum.CONFIRM) {
          this.activateSubscription(true);
        }
      });
      return;
    }

    this.activateSubscriptionRequest$.next(true);
    this.toasterService.notifyRequestMessage(
      {key: 'subscriptionActivationRequest', message: 'SUBSCRIPTION_ACTIVATION.SUBSCRIPTION_IS_ACTIVATED'}
    );

    if (this.isFreePlan) {
      this.administrationsApiService.updateCompanyProfileSbcManagement(this.getUpdatedCompanyProfile(), '', true)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          interval(5000)
            .pipe(
              startWith(0),
              takeWhile(() => {
                return this.companyProfile.status === 'PAUSED'
                  || this.companyProfile.subscriptionManagement.subscriptionStatus !== 'ACTIVATED';
              }),
              switchMap(() => this.administrationsApiService.getCompanyProfile()),
              takeUntil(this.destroy$)
            )
            .subscribe((profile: CompanyProfile) => {
              if (profile.status !== 'PAUSED' && profile.subscriptionManagement.subscriptionStatus === 'ACTIVATED') {
                this.toasterService.hideRequestMessage('subscriptionActivationRequest');
                this.activateSubscriptionRequest$.next(false);
                this.closeOnSuccess();
              }
            });
        }, () => {
          this.toasterService.hideRequestMessage('subscriptionActivationRequest');
          this.activateSubscriptionRequest$.next(false);
        });
      return;
    }

    if (!this.isFreePlan) {
      const failedCallback = () => {
        this.toasterService.hideRequestMessage('subscriptionActivationRequest');
        this.activateSubscriptionRequest$.next(false);
        this.activationError = true;
      };
      this.stripePaymentService.confirmPayment(failedCallback);
    }
  }

  public validatePlan(): boolean {
    return this.subscriptionOptionsComponentRef.validatePlan();
  }

  public startActivationSubscription(): void {
    if (this.companyProfile.billingDetails.shopifyConfirmationUrl) {
      window.open(this.companyProfile.billingDetails.shopifyConfirmationUrl);
      return;
    }
    this.activeTab$.next(SubscriptionActivationTabEnum.SUBSCRIPTION_OPTIONS);
  }

  public closeOnSuccess(): void {
    this.dialogRef.close();
    this.router.navigate(['/']).then(() => {
      this.hrmService.getUserProfileAndBlueprintsV2(this.authService.getUser().id, false, true)
        .subscribe();
    });
    this.toasterService.notify({
      type: 'activate-subscription',
      icon: 'checkmark-circle',
      title: 'SUBSCRIPTION_PLAN.YOUR_SUBSCRIPTION_IS_NOW_ACTIVE',
      message: 'SUBSCRIPTION_PLAN.YOUR_SUBSCRIPTION_IS_NOW_ACTIVE_DESCR',
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  get isFreePlan(): boolean {
    return this.subscriptionPlanControl.value === SubscriptionPlanEnum.FREE;
  }

  get isPlanControlValueTeamPlan(): boolean {
    return this.subscriptionPlanControl.value === SubscriptionPlanEnum.TEAM_MONTHLY;
  }

  get isPlanControlValueSoloPlan(): boolean {
    return this.subscriptionPlanControl.value === SubscriptionPlanEnum.SOLO_MONTHLY;
  }

  get isPlanControlValueBasicPlan(): boolean {
    return this.subscriptionPlanControl.value === SubscriptionPlanEnum.BASIC_SUBSCRIPTION_MONTHLY;
  }

  get isPlanControlValueStandardPlan(): boolean {
    return this.subscriptionPlanControl.value === SubscriptionPlanEnum.STANDARD_SUBSCRIPTION_MONTHLY;
  }

  get isPlanControlValueProPlan(): boolean {
    return this.subscriptionPlanControl.value === SubscriptionPlanEnum.PRO_SUBSCRIPTION_MONTHLY;
  }

  get inventoryManagementEnabled(): boolean {
    return this.form.get('subscriptionManagement').get('extensionsUser').get('inventoryManagement').value;
  }

  get timeTrackingEnabled(): boolean {
    return this.form.get('subscriptionManagement').get('extensionsUser').get('timeTracking').value;
  }

  get shopifyBilling(): boolean {
    if (!this.companyProfile) { return false; }
    return this.companyProfile.billingDetails.paymentMethod === 'SHOPIFY';
  }

  // get billingFrequencyControl(): FormControl { return this.form.get('billingDetails').get('billingFrequency') as FormControl; }
  get subscriptionPlanControl(): FormControl { return this.form.get('subscriptionManagement').get('subscriptionPlan') as FormControl; }

}
